import { useApplicationContext } from "@src/context/ApplicationContext";
import { useComponentNameContext } from "@src/context/ComponentNameContext";
import ClientLogsService from "@src/services/client-logs.service";
import logger from "@src/utils/logger";
import { useLocation } from "@uhg-abyss/web/tools/reactRouterTools";
import { useEffect } from "react";

/**
 * Hook that automatically detects clicks on elements with an id attribute
 * and logs them via ClientLogsService.
 *
 * Only logs clicks on elements with ids starting with 'obc'.
 * Skips logging if userUuid is not available.
 * Uses component name from ComponentContext if available, falls back to route pathname.
 */
export const useClickLogging = () => {
  const { data } = useApplicationContext();
  const { componentName } = useComponentNameContext();
  const location = useLocation();

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Guard: Skip if userUuid is not available
      if (!data.userUuid) {
        return;
      }

      // Find the closest element with an id or data-id attribute
      const elementWithId = target.closest("[id], [data-id]") as HTMLElement | null;

      // Only log if an element with an id or data-id was found
      if (!elementWithId) {
        return;
      }

      // Get id from either the id attribute or data-id attribute
      const elementId = elementWithId.id || elementWithId.dataset.id;

      // Only proceed if we have an id
      if (!elementId) {
        return;
      }
      // Only log if id starts with 'obc'
      if (!elementId.startsWith("obc")) {
        return;
      }

      // // Use component name if available, fallback to route pathname
      const location_param =
        componentName && componentName !== "Unknown" ? componentName : location.pathname;

      // Get the readable label from data-obc-label attribute
      const readableLabel = elementWithId.dataset.obcLabel;

      // Log the click asynchronously without blocking user interaction
      ClientLogsService.logClick(
        data.userUuid,
        location_param,
        elementId,
        readableLabel || undefined,
      ).catch((err) => {
        logger.error({
          text: "Failed to log click",
          error: err instanceof Error ? err : undefined,
          location: "useClickLogging",
        });
      });
    };

    // Attach the click listener to the document
    document.addEventListener("click", handleClick);

    // Cleanup the listener on unmount
    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [data.userUuid, componentName, location.pathname]);
};
